/**
 * @fileoverview Sort and filter, behind one button that says how many filters are on.
 *
 * The order came in here from the toolbar, where a dropdown took a whole box
 * on a phone for a setting chosen once; it is the sheet's last question now.
 * The week's marks are chips here (the shared Chip, ui/Chip), in the weather
 * bar's words ("Chilly Tue night"), which replaced a filter by severity band
 * ("worth a look") that said neither what nor when.
 *
 * There were five dropdowns and a search box stacked above the first plant —
 * more chrome than garden on a phone, and all of it weighted the same whether
 * or not it was doing anything. Searching and switching view are things you do
 * constantly; narrowing by bed, stage, status or date is something you do
 * occasionally and then want to see the effect of.
 *
 * So the occasional ones live in a sheet, and the button carries a count, so a
 * garden that is quietly hiding half itself says so on the surface.
 *
 * Where: PlantToolbar's sort and filter button.
 * API: none; the week's lines come with the brief (PlantGrid).
 * Settings: PLANTS_WIDE_MEDIA_QUERY (from the side at 768px and up, from
 * the bottom below); the choices in plantFilterOptions.
 * Words (en/es): plants.toolbar.* (sortAndFilter, sortAndFilterCount,
 * status, publicOn, canPlantNow, locationLabel, allLocations, stageLabel, allStages,
 * thisWeek, thisWeekHint, dateLabel, dateFrom, dateTo, sortBy, archiveLink,
 * archiveLinkHint, showPlants, showPlantsOne, clearFilters).
 *
 * @module PlantFilterSheet
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Archive, SlidersHorizontal } from 'lucide-react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from '@/components/ui/sheet';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Switch } from '@/shared/components/ui/switch';
import { Chip } from '@/shared/components/ui/Chip';
import { Pill } from '@/shared/components/ui/Pill';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { cn } from '@/lib/utils';
import type { PlantViewSettings, PlantStatusFilter, DateRangePreset, PlantSort } from '../lib/plantViews';
import type { BriefWeekLine } from '@/shared/api/todayBrief';
import { NO_LINE_FILTER, weekLineFilter, weekLineIcon, weekLineInk, weekLineIsFilter } from '../lib/weekLineLook';
import { SORTS, STATUSES, DATE_RANGES } from './plantFilterOptions';
import { PLANTS_WIDE_MEDIA_QUERY } from '@/config/tunables/plantList';

/** What the sheet offers, and where each choice goes. */
interface PlantFilterSheetProps {
  /** The garden's current view settings. */
  settings: PlantViewSettings;
  /** Applies one changed setting as it is chosen. */
  onChange: (patch: Partial<PlantViewSettings>) => void;
  /** The beds, for the bed filter. */
  locations: Array<{ id: number; name: string }>;
  /** The stages, for the stage filter. */
  stages: Array<{ id: number; name: string }>;
  /** How many filters are currently narrowing the garden. */
  activeCount: number;
  /** Clears every filter at once. */
  onClearAll: () => void;
  /** How many plants the current settings leave, for the button that closes the sheet. */
  shown: number;
  /** The week's lines (the brief's), offered as filters in the weather bar's words; a line with no plants is not offered. */
  lines: BriefWeekLine[];
}

/**
 * One labelled control, so the sheet reads as a list of questions. The label
 * names the control's group, so a screen reader says "Status" as it reaches
 * the dropdown, not only the value chosen in it.
 *
 * @param props.label - The question.
 * @param props.children - The control that answers it.
 */
const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => {
  const labelId = React.useId();
  return (
    <div role="group" aria-labelledby={labelId} className="space-y-1.5">
      <span id={labelId} className="text-sm font-medium">{label}</span>
      {children}
    </div>
  );
};

/**
 * The button with its count, and the sheet it opens.
 *
 * @param props - PlantFilterSheetProps.
 */
export const PlantFilterSheet: React.FC<PlantFilterSheetProps> = ({
  settings, onChange, locations, stages, activeCount, onClearAll, shown, lines,
}) => {
  const { reactTranslate: t } = useTranslation();
  // From the bottom on a phone, where a thumb is; from the side on a desk.
  const isWide = useMediaQuery(PLANTS_WIDE_MEDIA_QUERY);
  // Filters apply as they are chosen; the button at the bottom is how you say
  // you are done and see the result. Controlled, so the button can close it.
  const [open, setOpen] = React.useState(false);
  /** The week's lines that name plants: the ones a chip can narrow the garden to. */
  const weekChoices = lines.filter((line) => line.plantIds.length > 0);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          aria-label={activeCount > 0
            ? t('plants.toolbar.sortAndFilterCount', { count: activeCount })
            : t('plants.toolbar.sortAndFilter')}
          title={t('plants.toolbar.sortAndFilter')}
          className="relative h-10 w-10 shrink-0 p-0"
        >
          <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
          {/* Counts the chips under the toolbar, so a closed sheet can never hide a filter. */}
          {activeCount > 0 && (
            <Pill size="xs" tone="solid" className="absolute -right-1.5 -top-1.5">{activeCount}</Pill>
          )}
        </Button>
      </SheetTrigger>

      <SheetContent
        side={isWide ? 'right' : 'bottom'}
        className={cn('flex flex-col p-0', isWide ? 'w-[320px] sm:w-[360px]' : 'h-auto max-h-[88vh] rounded-t-xl')}
      >
        <SheetHeader className="px-6 pt-6">
          <SheetTitle>{t('plants.toolbar.sortAndFilter')}</SheetTitle>
        </SheetHeader>

        {/*
          * The questions scroll; the answer does not. "Show N plants" stays
          * pinned below them, so the count is in view as each choice is made.
          * What you came for comes first (which plants, where, what stage);
          * the order, chosen once and rarely changed, comes last.
          */}
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4">
          <Field label={t('plants.toolbar.status')}>
            <Select
              value={settings.status}
              onValueChange={(value) => onChange({ status: value as PlantStatusFilter })}
            >
              <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUSES.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{t(o.key)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <label className="flex min-h-11 items-center justify-between gap-3 text-sm font-medium">
            {t('plants.toolbar.publicOn')}
            <Switch checked={settings.public} onCheckedChange={(on) => onChange({ public: on })} />
          </label>

          <label className="flex min-h-11 items-center justify-between gap-3 text-sm font-medium">
            {t('plants.toolbar.canPlantNow')}
            <Switch checked={settings.canPlantNow} onCheckedChange={(on) => onChange({ canPlantNow: on })} />
          </label>

          {locations.length > 0 && (
            <Field label={t('plants.toolbar.locationLabel')}>
              <Select
                value={settings.location}
                onValueChange={(value) => onChange({ location: value })}
              >
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('plants.toolbar.allLocations')}</SelectItem>
                  {locations.map((l) => (
                    <SelectItem key={l.id} value={String(l.id)}>{l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}

          {stages.length > 0 && (
            <Field label={t('plants.toolbar.stageLabel')}>
              <Select
                value={settings.stage}
                onValueChange={(value) => onChange({ stage: value })}
              >
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('plants.toolbar.allStages')}</SelectItem>
                  {stages.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}

          {weekChoices.length > 0 && (
            <Field label={t('plants.toolbar.thisWeek')}>
              <p className="text-xs text-muted-foreground">{t('plants.toolbar.thisWeekHint')}</p>
              <div className="flex flex-wrap gap-1.5">
                {weekChoices.map((line) => {
                  const Icon = weekLineIcon(line);
                  // The same filter a tap on the weather bar's line sets: its plants, named by it.
                  const pressed = weekLineIsFilter(line, settings);
                  return (
                    <Chip
                      key={line.id}
                      pressed={pressed}
                      onClick={() => onChange(pressed ? NO_LINE_FILTER : weekLineFilter(line))}
                      icon={<Icon className={cn('h-3.5 w-3.5 shrink-0', weekLineInk(line))} />}
                    >
                      {line.title}
                    </Chip>
                  );
                })}
              </div>
            </Field>
          )}

          <Field label={t('plants.toolbar.dateLabel')}>
            <Select
              value={settings.dateRange}
              onValueChange={(value) => onChange({ dateRange: value as DateRangePreset })}
            >
              <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DATE_RANGES.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{t(o.key)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          {settings.dateRange === 'custom' && (
            <div className="flex items-end gap-2">
              <Field label={t('plants.toolbar.dateFrom')}>
                {/* Named for screen readers: the label above is a caption, not a
                    <label>, and a date box has no words of its own. */}
                <Input
                  type="date"
                  value={settings.dateFrom}
                  onChange={(e) => onChange({ dateFrom: e.target.value })}
                  aria-label={t('plants.toolbar.dateFrom')}
                  className="h-9"
                />
              </Field>
              <Field label={t('plants.toolbar.dateTo')}>
                <Input
                  type="date"
                  value={settings.dateTo}
                  onChange={(e) => onChange({ dateTo: e.target.value })}
                  aria-label={t('plants.toolbar.dateTo')}
                  className="h-9"
                />
              </Field>
            </div>
          )}

          <Field label={t('plants.toolbar.sortBy')}>
            <Select
              value={settings.sort}
              onValueChange={(value) => onChange({ sort: value as PlantSort })}
            >
              <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SORTS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{t(o.key)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          {/*
            * The way into the archive.
            *
            * The page existed and nothing linked to it, so a plant taken out of
            * the garden could be filtered back into view but never restored or
            * removed for good — the only door was typing the address. This is
            * the sheet somebody opens when they are wondering where a plant
            * went, which makes it the right place for the door.
            */}
          <Link
            to="/plants/archive"
            onClick={() => setOpen(false)}
            className="flex items-center justify-between gap-2 rounded-sm border px-3 py-2 text-sm hover:bg-muted"
          >
            <span>
              {t('plants.toolbar.archiveLink')}
              <span className="block text-xs text-muted-foreground">
                {t('plants.toolbar.archiveLinkHint')}
              </span>
            </span>
            <Archive className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          </Link>
        </div>

        <div className={cn('grid gap-2 border-t px-6 py-4', activeCount > 0 ? 'grid-cols-2' : 'grid-cols-1')}>
          <Button onClick={() => setOpen(false)}>
            {shown === 1 ? t('plants.toolbar.showPlantsOne') : t('plants.toolbar.showPlants', { count: shown })}
          </Button>
          {activeCount > 0 && (
            <Button variant="outline" onClick={onClearAll}>
              {t('plants.toolbar.clearFilters')}
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
